import { useEffect, useState, useRef } from "react";
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import {
  getIntradayEvolution,
  getIntradayPositionPerformance,
  type IntradayEvolutionPoint,
  type IntradayPositionPerformance,
} from "../api/client";
import { format } from "date-fns";

const REFRESH_INTERVAL_MS = 30_000; // auto-refresh every 30 seconds

interface Props {
  portfolioId: number | null;
}

export default function IntradayEvolutionPage({ portfolioId }: Props) {
  const [data, setData] = useState<IntradayEvolutionPoint[]>([]);
  const [posPerf, setPosPerf] = useState<IntradayPositionPerformance[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = async (id: number) => {
    setLoading(true);
    setError(null);
    try {
      const [evRes, perfRes] = await Promise.all([
        getIntradayEvolution(id),
        getIntradayPositionPerformance(id).catch(() => ({ data: [] })),
      ]);
      setData(evRes.data);
      setPosPerf(perfRes.data);
      setLastUpdated(new Date());
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail ?? "Failed to load intraday evolution");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!portfolioId) return;
    load(portfolioId);

    intervalRef.current = setInterval(() => {
      load(portfolioId);
    }, REFRESH_INTERVAL_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [portfolioId]);

  const formatted = data.map((d) => ({
    ...d,
  }));

  const latestValue = formatted.length > 0 ? formatted[formatted.length - 1].total_value : null;
  const firstValue = formatted.length > 0 ? formatted[0].total_value : null;

  // 3-day change (over entire data window)
  const change3d = latestValue != null && firstValue != null ? latestValue - firstValue : null;
  const changePct3d = change3d != null && firstValue ? (change3d / firstValue) * 100 : null;

  // 1-day change: from first bar of today
  const todayStr = format(new Date(), "yyyy-MM-dd");
  const todayBars = formatted.filter((d) => d.timestamp.slice(0, 10) === todayStr);
  const firstValueToday = todayBars.length > 0 ? todayBars[0].total_value : null;
  const change1d = latestValue != null && firstValueToday != null ? latestValue - firstValueToday : null;
  const changePct1d = change1d != null && firstValueToday ? (change1d / firstValueToday) * 100 : null;

  // Day boundary reference lines for the chart
  const dayBoundaries = formatted.reduce<{ timestamp: string; dateStr: string }[]>(
    (acc, bar, i) => {
      if (i === 0) return acc;
      const barDate = bar.timestamp.slice(0, 10);
      const prevDate = formatted[i - 1].timestamp.slice(0, 10);
      if (barDate !== prevDate) {
        acc.push({
          timestamp: bar.timestamp,
          dateStr: format(new Date(bar.timestamp), "MMM d"),
        });
      }
      return acc;
    },
    [],
  );

  const formatCurrency = (v: number) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(v);

  const formatPct = (v: number | null) =>
    v == null ? "—" : `${v >= 0 ? "+" : ""}${v.toFixed(2)}%`;

  const changeColor = (v: number | null) => (v == null ? "#334155" : v >= 0 ? "#16a34a" : "#dc2626");

  return (
    <div>
      <h2 style={{ margin: "0 0 20px", color: "#1e293b" }}>Intraday Portfolio Evolution</h2>

      {!portfolioId && (
        <div style={{ background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 8, padding: 16, marginBottom: 20 }}>
          ⚠️ Please select a portfolio to view its intraday evolution.
        </div>
      )}

      {portfolioId && (
        <>
          {/* Summary cards */}
          <div style={{ display: "flex", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
            <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 16, minWidth: 180 }}>
              <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>Current Value</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: "#1e293b" }}>
                {latestValue != null ? formatCurrency(latestValue) : "—"}
              </div>
            </div>
            {change1d != null && (
              <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 16, minWidth: 180 }}>
                <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>1-Day Change</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: changeColor(change1d) }}>
                  {change1d >= 0 ? "▲" : "▼"} {formatCurrency(Math.abs(change1d))}
                  <span style={{ fontSize: 14, marginLeft: 6 }}>({formatPct(changePct1d)})</span>
                </div>
              </div>
            )}
            {change3d != null && (
              <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 16, minWidth: 180 }}>
                <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>3-Day Change</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: changeColor(change3d) }}>
                  {change3d >= 0 ? "▲" : "▼"} {formatCurrency(Math.abs(change3d))}
                  <span style={{ fontSize: 14, marginLeft: 6 }}>({formatPct(changePct3d)})</span>
                </div>
              </div>
            )}
            <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 16, minWidth: 180, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
              <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>Last Updated</div>
              <div style={{ fontSize: 14, color: "#334155" }}>
                {lastUpdated ? format(lastUpdated, "HH:mm:ss") : "—"}
              </div>
              <div style={{ fontSize: 11, color: "#94a3b8" }}>Auto-refreshes every 30s</div>
            </div>
          </div>

          {/* Chart */}
          <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 20, marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 16, gap: 12 }}>
              <h3 style={{ margin: 0, color: "#1e293b" }}>Portfolio Value — Last 3 Days (1-min resolution)</h3>
              {loading && <span style={{ fontSize: 13, color: "#94a3b8" }}>Refreshing…</span>}
            </div>

            {error && (
              <div style={{ color: "#dc2626", fontSize: 13, marginBottom: 12 }}>{error}</div>
            )}

            {!loading && !error && formatted.length === 0 && (
              <p style={{ color: "#94a3b8" }}>
                No intraday data available. Subscribe to symbols in the Intraday page to start capturing data.
              </p>
            )}

            {formatted.length > 0 && (
              <ResponsiveContainer width="100%" height={360}>
                <ComposedChart data={formatted} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="timestamp"
                    tick={{ fontSize: 11 }}
                    interval={Math.max(0, Math.floor(formatted.length / 12) - 1)}
                    tickFormatter={(v: string) => format(new Date(v), "HH:mm")}
                  />
                  <YAxis
                    tickFormatter={(v) => formatCurrency(v)}
                    tick={{ fontSize: 11 }}
                    width={100}
                    domain={["auto", "auto"]}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0]?.payload as typeof formatted[0];
                      return (
                        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 6, padding: "8px 12px", fontSize: 12 }}>
                          <div style={{ fontWeight: 600, marginBottom: 4 }}>{format(new Date(d.timestamp), "MMM d HH:mm")}</div>
                          <div>Value: <strong>{formatCurrency(d.total_value)}</strong></div>
                        </div>
                      );
                    }}
                  />
                  {/* Day separator lines */}
                  {dayBoundaries.map((b) => (
                    <ReferenceLine
                      key={b.timestamp}
                      x={b.timestamp}
                      stroke="#94a3b8"
                      strokeDasharray="4 4"
                      strokeWidth={1.5}
                      label={{
                        value: b.dateStr,
                        position: "insideTopRight",
                        fontSize: 10,
                        fill: "#64748b",
                        fontWeight: 600,
                      }}
                    />
                  ))}
                  <Line
                    type="monotone"
                    dataKey="total_value"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={false}
                    name="Portfolio Value"
                    connectNulls
                  />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Position performance table */}
          {posPerf.length > 0 && (
            <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 20 }}>
              <h3 style={{ margin: "0 0 16px", color: "#1e293b" }}>Position Intraday Performance</h3>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "#f1f5f9" }}>
                      {["Symbol", "Qty", "Latest Price", "1D Change", "1D P&L", "3D Change", "3D P&L"].map((h) => (
                        <th key={h} style={{ padding: "8px 12px", color: "#475569", fontWeight: 600, textAlign: h === "Symbol" ? "left" : "right", whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {posPerf.map((p) => (
                      <tr key={p.symbol} style={{ borderBottom: "1px solid #f1f5f9" }}>
                        <td style={{ padding: "7px 12px", fontFamily: "monospace", fontWeight: 600 }}>{p.symbol}</td>
                        <td style={{ padding: "7px 12px", textAlign: "right" }}>{p.quantity.toLocaleString()}</td>
                        <td style={{ padding: "7px 12px", textAlign: "right" }}>
                          {p.latest_price != null ? p.latest_price.toFixed(4) : "—"}
                        </td>
                        <td style={{ padding: "7px 12px", textAlign: "right", color: changeColor(p.change_1d_pct), fontWeight: 500 }}>
                          {formatPct(p.change_1d_pct)}
                        </td>
                        <td style={{ padding: "7px 12px", textAlign: "right", color: changeColor(p.change_1d_pnl) }}>
                          {p.change_1d_pnl != null ? (p.change_1d_pnl >= 0 ? "+" : "") + p.change_1d_pnl.toFixed(2) : "—"}
                        </td>
                        <td style={{ padding: "7px 12px", textAlign: "right", color: changeColor(p.change_3d_pct), fontWeight: 500 }}>
                          {formatPct(p.change_3d_pct)}
                        </td>
                        <td style={{ padding: "7px 12px", textAlign: "right", color: changeColor(p.change_3d_pnl) }}>
                          {p.change_3d_pnl != null ? (p.change_3d_pnl >= 0 ? "+" : "") + p.change_3d_pnl.toFixed(2) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

